import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeft, X, Headphones, User, FileText, MessageSquare, Loader2, Mail, Phone, CheckSquare } from 'lucide-react';
import { useSarLabAuth } from '../contexts/SarLabAuthContext';
import { useLiveSupport } from '../contexts/LiveSupportContext';
import { useWidgetConfig } from '../contexts/WidgetConfigContext';

interface LiveSupportModalProps {
  onClose: () => void;
  onSuccess: () => void;
  onBack: () => void;
}

const LiveSupportModal: React.FC<LiveSupportModalProps> = ({ onClose, onSuccess, onBack }) => {
  const { t } = useTranslation();
  const { currentPlayer } = useSarLabAuth();
  const { createTicket } = useLiveSupport();
  const { config } = useWidgetConfig();
  const [formData, setFormData] = useState<Record<string, string | boolean>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const isAuthenticated = !!currentPlayer;

  // Get primary color from config
  const primaryColor = config.window.primaryColor || '#6366f1';

  // Get sorted fields from config
  const fields = useMemo(() => {
    const configFields = config.prechat.fields || [];
    return [...configFields].sort((a, b) => a.sortOrder - b.sortOrder);
  }, [config.prechat.fields]);

  // Get field value
  const getFieldValue = (fieldName: string, fieldType: string): string | boolean => {
    if (formData[fieldName] !== undefined) {
      return formData[fieldName];
    }
    // Default value for authenticated user's name
    if (fieldName === 'name' && isAuthenticated) {
      return currentPlayer?.username || '';
    }
    // Default value for checkboxes
    if (fieldType === 'checkbox') {
      return false;
    }
    return '';
  };

  // Set field value
  const setFieldValue = (fieldName: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  // Get icon for field type
  const getFieldIcon = (fieldType: string, fieldName: string) => {
    if (fieldName === 'name') return <User className="w-4 h-4" />;
    if (fieldName === 'email') return <Mail className="w-4 h-4" />;
    if (fieldName === 'subject') return <FileText className="w-4 h-4" />;
    if (fieldName === 'message') return <MessageSquare className="w-4 h-4" />;
    if (fieldType === 'phone') return <Phone className="w-4 h-4" />;
    if (fieldType === 'checkbox') return <CheckSquare className="w-4 h-4" />;
    if (fieldType === 'select') return <FileText className="w-4 h-4" />;
    if (fieldType === 'textarea') return <MessageSquare className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate required fields
    for (const field of fields) {
      const isRequired = field.required === true || Number(field.required) === 1;
      const value = getFieldValue(field.name, field.type);

      // Skip name validation for authenticated users
      if (field.name === 'name' && isAuthenticated) continue;

      if (isRequired) {
        if (field.type === 'checkbox') {
          if (value !== true) {
            setError(`${field.label} ${t('support:prechat.checkboxRequired')}`);
            return;
          }
        } else if (typeof value === 'string' && !value.trim()) {
          setError(`${field.label} ${t('support:prechat.fieldRequired')}`);
          return;
        }
      }

      if ((field.type === 'text' || field.type === 'textarea') && typeof value === 'string' && value.trim()) {
        if (field.name === 'name' && value.trim().length < 2) {
          setError(t('support:prechat.nameMinLength'));
          return;
        }
        if (field.name === 'message' && value.trim().length < 10) {
          setError(t('support:prechat.messageMinLength'));
          return;
        }
      }
    }

    setIsLoading(true);

    try {
      // Get values for createTicket
      const subject = (getFieldValue('subject', 'select') as string) || '';
      const message = (getFieldValue('message', 'textarea') as string) || '';
      const name = isAuthenticated ? undefined : (getFieldValue('name', 'text') as string) || undefined;
      const email = isAuthenticated ? undefined : (getFieldValue('email', 'email') as string) || undefined;

      await createTicket(subject, message, name, email);
      onSuccess();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Bir hata olustu';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Render a single field based on its type
  const renderField = (field: typeof fields[0]) => {
    const isRequired = field.required === true || Number(field.required) === 1;
    const value = getFieldValue(field.name, field.type);

    // For authenticated users, show name as read-only
    if (field.name === 'name' && isAuthenticated) {
      return (
        <div key={field.id} className="bg-dark-700/30 rounded-lg p-3">
          <div className="flex items-center space-x-2 text-sm text-gray-400 mb-1">
            {getFieldIcon(field.type, field.name)}
            <span>{field.label}</span>
          </div>
          <p className="text-white font-medium">{currentPlayer?.username || 'Kullanici'}</p>
        </div>
      );
    }

    // Checkbox field
    if (field.type === 'checkbox') {
      return (
        <div key={field.id} className="flex items-start space-x-3">
          <input
            type="checkbox"
            id={field.id}
            checked={value === true}
            onChange={(e) => setFieldValue(field.name, e.target.checked)}
            className="mt-1 w-4 h-4 rounded border-dark-600/50 bg-dark-700/50 text-indigo-500 focus:ring-2 focus:ring-offset-0"
            style={{ accentColor: primaryColor }}
          />
          <label htmlFor={field.id} className="text-sm text-gray-300 cursor-pointer">
            {field.label}{isRequired && ' *'}
          </label>
        </div>
      );
    }

    // Select field
    if (field.type === 'select') {
      return (
        <div key={field.id}>
          <label className="flex items-center space-x-2 text-sm text-gray-400 mb-2">
            {getFieldIcon(field.type, field.name)}
            <span>{field.label}{isRequired && ' *'}</span>
          </label>
          <select
            value={value as string}
            onChange={(e) => setFieldValue(field.name, e.target.value)}
            className="w-full px-3 py-2 bg-dark-700/50 border border-dark-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:border-transparent"
            style={{ '--tw-ring-color': primaryColor } as React.CSSProperties}
          >
            <option value="">{t('support:prechat.selectSubject')}</option>
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );
    }

    // Textarea field
    if (field.type === 'textarea') {
      return (
        <div key={field.id}>
          <label className="flex items-center space-x-2 text-sm text-gray-400 mb-2">
            {getFieldIcon(field.type, field.name)}
            <span>{field.label}{isRequired && ' *'}</span>
          </label>
          <textarea
            value={value as string}
            onChange={(e) => setFieldValue(field.name, e.target.value)}
            placeholder={t('support:prechat.messagePlaceholder')}
            rows={4}
            className="w-full px-3 py-2 bg-dark-700/50 border border-dark-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent resize-none"
            style={{ '--tw-ring-color': primaryColor } as React.CSSProperties}
          />
        </div>
      );
    }

    const inputType = field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text';
    const placeholder = field.name === 'name' ? t('support:prechat.namePlaceholder')
      : field.name === 'email' ? t('support:prechat.emailPlaceholder')
      : `${field.label} girin...`;

    return (
      <div key={field.id}>
        <label className="flex items-center space-x-2 text-sm text-gray-400 mb-2">
          {getFieldIcon(field.type, field.name)}
          <span>{field.label}{isRequired && ' *'}</span>
        </label>
        <input
          type={inputType}
          value={value as string}
          onChange={(e) => setFieldValue(field.name, e.target.value)}
          placeholder={placeholder}
          className="w-full px-3 py-2 bg-dark-700/50 border border-dark-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent"
          style={{ '--tw-ring-color': primaryColor } as React.CSSProperties}
        />
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-4 border-b border-dark-700/50">
        <div className="flex items-center space-x-3">
          <button
            onClick={onBack}
            className="p-1 hover:bg-dark-700/50 rounded transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4 text-gray-400" />
          </button>
          <div className="flex items-center space-x-2">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ backgroundColor: primaryColor }}
            >
              <Headphones className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-medium text-white">{t('support:chat.supportTitle')}</span>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-dark-700/50 rounded transition-colors duration-200"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Greeting message at the top */}
        {(config.prechat.greetingMessage || config.window.greetingMessage) && (
          <p className="text-sm text-gray-300 text-center pb-2 border-b border-dark-700/30">
            {config.prechat.greetingMessage || config.window.greetingMessage}
          </p>
        )}

        {/* Dynamic fields */}
        {fields.map(renderField)}

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-500/10 border border-red-500/20 text-red-400 px-3 py-2 rounded-lg text-sm"
          >
            {error}
          </motion.div>
        )}

        <motion.button
          type="submit"
          disabled={isLoading}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full py-3 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-all duration-300 flex items-center justify-center space-x-2"
          style={{ backgroundColor: primaryColor }}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>{t('support:prechat.connecting')}</span>
            </>
          ) : (
            <>
              <Headphones className="w-4 h-4" />
              <span>{t('support:prechat.startChat')}</span>
            </>
          )}
        </motion.button>

        <p className="text-xs text-gray-500 text-center">
          {t('support:prechat.supportTeam')}
        </p>
      </form>
    </div>
  );
};

export default LiveSupportModal;
